import request from '@/Network/Requests/request'
import Service from '@/Network/Services/Service'
import { AuthorizationResourcesAndSubjects } from '@/types/authorization'
import {
  AuthorizationRelationResponse,
  AuthorizationRelationsResponse,
  AuthorizationRelationPaginatedResponse
} from '@/Network/Types/Responses/AuthorizationResponses'
import {
  PermissionTreeItem,
  AccessibleCompanyOption
} from '@/Network/Types/Responses/PermissionResponses'
import { UpsertPermissionsRequest } from '../Types/Requests/AuthorizationRequests'
import { SubjectCandidateResponse } from '@/Network/Types/Responses/AuthorizationResponses'
import { ServiceError, isServiceError } from '@/utils/apiError'

type PermissionListQuery = { q?: string; limit?: number; offset?: number }

export default class PermissionService extends Service {
  prefix: string

  constructor() {
    super()

    this.prefix = '/permissions'
  }

  async addPermissions(
    resourceId: number,
    resourceType: AuthorizationResourcesAndSubjects,
    payload: UpsertPermissionsRequest,
    companyId: number
  ): Promise<AuthorizationRelationResponse[]> {
    let url = ''
    if (companyId && resourceType !== AuthorizationResourcesAndSubjects.Company) {
      url = `/company/${companyId}`
    }
    try {
      const { data } = await request.post<AuthorizationRelationResponse[]>(
        `${this.getCompletePath()}/shared/${resourceType}/${resourceId}${url}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível compartilhar as permissões.')
    }
  }

  async upsertPermissions(
    resourceId: number,
    resourceType: AuthorizationResourcesAndSubjects,
    payload: UpsertPermissionsRequest,
    companyId: number
  ): Promise<AuthorizationRelationResponse[]> {
    let url = ''
    if (companyId && resourceType !== AuthorizationResourcesAndSubjects.Company) {
      url = `/company/${companyId}`
    }
    try {
      const { data } = await request.put<AuthorizationRelationResponse[]>(
        `${this.getCompletePath()}/shared/${resourceType}/${resourceId}${url}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível atualizar as permissões.')
    }
  }

  async listPermissions(
    resourceType: AuthorizationResourcesAndSubjects,
    resourceId: number,
    companyId?: number,
    search?: string,
    limit?: number,
    offset?: number
  ): Promise<AuthorizationRelationPaginatedResponse> {
    let url = ''
    if (companyId && resourceType !== AuthorizationResourcesAndSubjects.Company) {
      url = `/company/${companyId}`
    }

    const queryParams: PermissionListQuery = {}
    if (search) queryParams.q = search
    if (limit) queryParams.limit = limit
    if (offset) queryParams.offset = offset

    try {
      const { data } = await request.get<AuthorizationRelationPaginatedResponse>(
        `${this.getCompletePath()}/${resourceType}/${resourceId}${url}`,
        queryParams
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as permissões.')
    }
  }

  async listAllRelations(): Promise<AuthorizationRelationsResponse> {
    try {
      const { data } = await request.get<AuthorizationRelationsResponse>(
        `${this.getCompletePath()}/relations`
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os tipos de permissão.')
    }
  }

  async listResourceCandidates(
    resourceType: AuthorizationResourcesAndSubjects,
    resourceId: number,
    companyId: number,
    search?: string,
    limit?: number,
    offset?: number
  ): Promise<SubjectCandidateResponse[]> {
    const queryParams: PermissionListQuery = {}
    if (search) queryParams.q = search
    if (limit) queryParams.limit = limit
    if (offset) queryParams.offset = offset

    let endpoint = ''
    if (
      resourceType === AuthorizationResourcesAndSubjects.Company ||
      resourceType === AuthorizationResourcesAndSubjects.DsAttendanceCompany ||
      resourceType === AuthorizationResourcesAndSubjects.NpsCompany ||
      resourceType === AuthorizationResourcesAndSubjects.PostStationCompany ||
      resourceType === AuthorizationResourcesAndSubjects.StationExceptionalitiesCompany
    ) {
      endpoint = `${this.getCompletePath()}/candidates/${resourceType}/${resourceId}`
    } else {
      endpoint = `${this.getCompletePath()}/candidates/${resourceType}/${resourceId}/company/${companyId}`
    }
    try {
      const { data } = await request.get<SubjectCandidateResponse[]>(endpoint, queryParams)
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar os usuários e grupos para compartilhar.')
    }
  }

  // ===========================================================================
  // DS ATTENDANCE (nível schedule)
  // ===========================================================================

  async addDsAttendancePermissions(
    resourceId: number,
    companyId: number,
    payload: UpsertPermissionsRequest
  ): Promise<AuthorizationRelationResponse[]> {
    try {
      const { data } = await request.post<AuthorizationRelationResponse[]>(
        `${this.getCompletePath()}/shared/ds_attendance/${resourceId}/company/${companyId}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível adicionar as permissões do registro de plantão.')
    }
  }

  async upsertDsAttendancePermissions(
    resourceId: number,
    companyId: number,
    payload: UpsertPermissionsRequest
  ): Promise<AuthorizationRelationResponse[]> {
    try {
      const { data } = await request.put<AuthorizationRelationResponse[]>(
        `${this.getCompletePath()}/shared/ds_attendance/${resourceId}/company/${companyId}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível atualizar as permissões do registro de plantão.')
    }
  }

  async listDsAttendancePermissions(
    resourceId: number,
    companyId: number,
    search?: string,
    limit?: number,
    offset?: number
  ): Promise<AuthorizationRelationPaginatedResponse> {
    const queryParams: PermissionListQuery = {}
    if (search) queryParams.q = search
    if (limit) queryParams.limit = limit
    if (offset) queryParams.offset = offset

    try {
      const { data } = await request.get<AuthorizationRelationPaginatedResponse>(
        `${this.getCompletePath()}/ds_attendance/${resourceId}/company/${companyId}`,
        queryParams
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as permissões do registro de plantão.')
    }
  }

  // ===========================================================================
  // DS ATTENDANCE COMPANY (nível empresa)
  // ===========================================================================

  async addDsAttendanceCompanyPermissions(
    resourceId: number,
    payload: UpsertPermissionsRequest
  ): Promise<AuthorizationRelationResponse[]> {
    try {
      const { data } = await request.post<AuthorizationRelationResponse[]>(
        `${this.getCompletePath()}/shared/ds_attendance_company/${resourceId}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível adicionar as permissões do registro de plantão.')
    }
  }

  async upsertDsAttendanceCompanyPermissions(
    resourceId: number,
    payload: UpsertPermissionsRequest
  ): Promise<AuthorizationRelationResponse[]> {
    try {
      const { data } = await request.put<AuthorizationRelationResponse[]>(
        `${this.getCompletePath()}/shared/ds_attendance_company/${resourceId}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível atualizar as permissões do registro de plantão.')
    }
  }

  async listDsAttendanceCompanyPermissions(
    resourceId: number,
    search?: string,
    limit?: number,
    offset?: number
  ): Promise<AuthorizationRelationPaginatedResponse> {
    const queryParams: PermissionListQuery = {}
    if (search) queryParams.q = search
    if (limit) queryParams.limit = limit
    if (offset) queryParams.offset = offset

    try {
      const { data } = await request.get<AuthorizationRelationPaginatedResponse>(
        `${this.getCompletePath()}/ds_attendance_company/${resourceId}`,
        queryParams
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as permissões do registro de plantão.')
    }
  }

  // ===========================================================================
  // DS ATTENDANCE HEALTH UNIT (nível unidade de saúde)
  // ===========================================================================

  async addDsAttendanceHealthUnitPermissions(
    resourceId: number,
    companyId: number,
    payload: UpsertPermissionsRequest
  ): Promise<AuthorizationRelationResponse[]> {
    try {
      const { data } = await request.post<AuthorizationRelationResponse[]>(
        `${this.getCompletePath()}/shared/ds_attendance_health_unit/${resourceId}/company/${companyId}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível adicionar as permissões do registro de plantão.')
    }
  }

  async upsertDsAttendanceHealthUnitPermissions(
    resourceId: number,
    companyId: number,
    payload: UpsertPermissionsRequest
  ): Promise<AuthorizationRelationResponse[]> {
    try {
      const { data } = await request.put<AuthorizationRelationResponse[]>(
        `${this.getCompletePath()}/shared/ds_attendance_health_unit/${resourceId}/company/${companyId}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível atualizar as permissões do registro de plantão.')
    }
  }

  async listDsAttendanceHealthUnitPermissions(
    resourceId: number,
    companyId: number,
    search?: string,
    limit?: number,
    offset?: number
  ): Promise<AuthorizationRelationPaginatedResponse> {
    const queryParams: PermissionListQuery = {}
    if (search) queryParams.q = search
    if (limit) queryParams.limit = limit
    if (offset) queryParams.offset = offset

    try {
      const { data } = await request.get<AuthorizationRelationPaginatedResponse>(
        `${this.getCompletePath()}/ds_attendance_health_unit/${resourceId}/company/${companyId}`,
        queryParams
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as permissões do registro de plantão.')
    }
  }

  // ===========================================================================
  // NPS COMPANY (nível empresa)
  // ===========================================================================

  async addNpsCompanyPermissions(
    resourceId: number,
    payload: UpsertPermissionsRequest
  ): Promise<AuthorizationRelationResponse[]> {
    try {
      const { data } = await request.post<AuthorizationRelationResponse[]>(
        `${this.getCompletePath()}/shared/nps_company/${resourceId}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível adicionar as permissões do NPS.')
    }
  }

  async upsertNpsCompanyPermissions(
    resourceId: number,
    payload: UpsertPermissionsRequest
  ): Promise<AuthorizationRelationResponse[]> {
    try {
      const { data } = await request.put<AuthorizationRelationResponse[]>(
        `${this.getCompletePath()}/shared/nps_company/${resourceId}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível atualizar as permissões do NPS.')
    }
  }

  async listNpsCompanyPermissions(
    resourceId: number,
    search?: string,
    limit?: number,
    offset?: number
  ): Promise<AuthorizationRelationPaginatedResponse> {
    const queryParams: PermissionListQuery = {}
    if (search) queryParams.q = search
    if (limit) queryParams.limit = limit
    if (offset) queryParams.offset = offset

    try {
      const { data } = await request.get<AuthorizationRelationPaginatedResponse>(
        `${this.getCompletePath()}/nps_company/${resourceId}`,
        queryParams
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as permissões do NPS.')
    }
  }

  // ===========================================================================
  // NPS HEALTH UNIT (nível unidade de saúde)
  // ===========================================================================

  async addNpsHealthUnitPermissions(
    resourceId: number,
    companyId: number,
    payload: UpsertPermissionsRequest
  ): Promise<AuthorizationRelationResponse[]> {
    try {
      const { data } = await request.post<AuthorizationRelationResponse[]>(
        `${this.getCompletePath()}/shared/nps_health_unit/${resourceId}/company/${companyId}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível adicionar as permissões do NPS.')
    }
  }

  async upsertNpsHealthUnitPermissions(
    resourceId: number,
    companyId: number,
    payload: UpsertPermissionsRequest
  ): Promise<AuthorizationRelationResponse[]> {
    try {
      const { data } = await request.put<AuthorizationRelationResponse[]>(
        `${this.getCompletePath()}/shared/nps_health_unit/${resourceId}/company/${companyId}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível atualizar as permissões do NPS.')
    }
  }

  async listNpsHealthUnitPermissions(
    resourceId: number,
    companyId: number,
    search?: string,
    limit?: number,
    offset?: number
  ): Promise<AuthorizationRelationPaginatedResponse> {
    const queryParams: PermissionListQuery = {}
    if (search) queryParams.q = search
    if (limit) queryParams.limit = limit
    if (offset) queryParams.offset = offset

    try {
      const { data } = await request.get<AuthorizationRelationPaginatedResponse>(
        `${this.getCompletePath()}/nps_health_unit/${resourceId}/company/${companyId}`,
        queryParams
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as permissões do NPS.')
    }
  }

  // ===========================================================================
  // POST-STATION COMPANY (nível empresa)
  // ===========================================================================

  async addPostStationCompanyPermissions(
    resourceId: number,
    payload: UpsertPermissionsRequest
  ): Promise<AuthorizationRelationResponse[]> {
    try {
      const { data } = await request.post<AuthorizationRelationResponse[]>(
        `${this.getCompletePath()}/shared/post_station_company/${resourceId}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível adicionar as permissões do pós-plantão.')
    }
  }

  async upsertPostStationCompanyPermissions(
    resourceId: number,
    payload: UpsertPermissionsRequest
  ): Promise<AuthorizationRelationResponse[]> {
    try {
      const { data } = await request.put<AuthorizationRelationResponse[]>(
        `${this.getCompletePath()}/shared/post_station_company/${resourceId}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível atualizar as permissões do pós-plantão.')
    }
  }

  async listPostStationCompanyPermissions(
    resourceId: number,
    search?: string,
    limit?: number,
    offset?: number
  ): Promise<AuthorizationRelationPaginatedResponse> {
    const queryParams: PermissionListQuery = {}
    if (search) queryParams.q = search
    if (limit) queryParams.limit = limit
    if (offset) queryParams.offset = offset

    try {
      const { data } = await request.get<AuthorizationRelationPaginatedResponse>(
        `${this.getCompletePath()}/post_station_company/${resourceId}`,
        queryParams
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as permissões do pós-plantão.')
    }
  }

  // ===========================================================================
  // POST-STATION HEALTH UNIT (nível unidade de saúde)
  // ===========================================================================

  async addPostStationHealthUnitPermissions(
    resourceId: number,
    companyId: number,
    payload: UpsertPermissionsRequest
  ): Promise<AuthorizationRelationResponse[]> {
    try {
      const { data } = await request.post<AuthorizationRelationResponse[]>(
        `${this.getCompletePath()}/shared/post_station_health_unit/${resourceId}/company/${companyId}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível adicionar as permissões do pós-plantão.')
    }
  }

  async upsertPostStationHealthUnitPermissions(
    resourceId: number,
    companyId: number,
    payload: UpsertPermissionsRequest
  ): Promise<AuthorizationRelationResponse[]> {
    try {
      const { data } = await request.put<AuthorizationRelationResponse[]>(
        `${this.getCompletePath()}/shared/post_station_health_unit/${resourceId}/company/${companyId}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível atualizar as permissões do pós-plantão.')
    }
  }

  async listPostStationHealthUnitPermissions(
    resourceId: number,
    companyId: number,
    search?: string,
    limit?: number,
    offset?: number
  ): Promise<AuthorizationRelationPaginatedResponse> {
    const queryParams: PermissionListQuery = {}
    if (search) queryParams.q = search
    if (limit) queryParams.limit = limit
    if (offset) queryParams.offset = offset

    try {
      const { data } = await request.get<AuthorizationRelationPaginatedResponse>(
        `${this.getCompletePath()}/post_station_health_unit/${resourceId}/company/${companyId}`,
        queryParams
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as permissões do pós-plantão.')
    }
  }

  async checkPermissionImpact(payload: {
    resource_id: number
    resource_type: AuthorizationResourcesAndSubjects
    subjects: { subject_id: number; subject_type: string }[]
  }): Promise<any[]> {
    try {
      const { data } = await request.post<any[]>(`${this.prefix}/check-impact`, payload)
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível verificar o impacto da alteração de permissões.')
    }
  }

  async getSubjectPermissionsTree(
    subjectId: number,
    subjectType: AuthorizationResourcesAndSubjects,
    companyIds?: number[],
    offset?: number,
    limit?: number
  ): Promise<PermissionTreeItem[]> {
    const endpointPrefix =
      subjectType === AuthorizationResourcesAndSubjects.User ? 'users' : 'groups'

    const params: { companyIds?: string; offset?: number; limit?: number } = {}
    if (companyIds && companyIds.length > 0) params.companyIds = companyIds.join(',')
    if (offset) params.offset = offset
    if (limit) params.limit = limit

    try {
      const response = await request.get<PermissionTreeItem[]>(
        `${this.getCompletePath()}/${endpointPrefix}/${subjectId}/permissions-tree`,
        params
      )

      if (response && Array.isArray(response.data)) {
        return response.data
      }

      throw new ServiceError('Resposta inválida ao carregar a árvore de permissões.', response)
    } catch (e) {
      if (isServiceError(e)) throw e
      throw this.handleError(e, 'Não foi possível carregar a árvore de permissões.')
    }
  }

  async getSubjectAccessibleCompanies(
    subjectId: number,
    subjectType: AuthorizationResourcesAndSubjects
  ): Promise<AccessibleCompanyOption[]> {
    if (
      subjectType !== AuthorizationResourcesAndSubjects.User &&
      subjectType !== AuthorizationResourcesAndSubjects.Group
    ) {
      return []
    }

    const endpointPrefix =
      subjectType === AuthorizationResourcesAndSubjects.User ? 'users' : 'groups'

    try {
      const response = await request.get<AccessibleCompanyOption[]>(
        `${this.getCompletePath()}/${endpointPrefix}/${subjectId}/accessible-companies`
      )

      if (response && Array.isArray(response.data)) {
        return response.data
      }

      throw new ServiceError('Resposta inválida ao carregar a lista de empresas.', response)
    } catch (e) {
      if (isServiceError(e)) throw e
      throw this.handleError(e, 'Não foi possível carregar a lista de empresas.')
    }
  }

  // --- Station Exceptionalities ---

  async addStationExceptionalityToCompany(
    resourceId: number,
    payload: UpsertPermissionsRequest
  ): Promise<AuthorizationRelationResponse[]> {
    try {
      const { data } = await request.post<AuthorizationRelationResponse[]>(
        `${this.getCompletePath()}/shared/station_exceptionalities_company/${resourceId}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível adicionar as permissões de excepcionalidades.')
    }
  }

  async upsertStationExceptionalityToCompany(
    resourceId: number,
    payload: UpsertPermissionsRequest
  ): Promise<AuthorizationRelationResponse[]> {
    try {
      const { data } = await request.put<AuthorizationRelationResponse[]>(
        `${this.getCompletePath()}/shared/station_exceptionalities_company/${resourceId}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível atualizar as permissões de excepcionalidades.')
    }
  }

  async listStationExceptionalitiesByCompany(
    resourceId: number,
    search?: string,
    limit?: number,
    offset?: number
  ): Promise<AuthorizationRelationPaginatedResponse> {
    const queryParams: PermissionListQuery = {}
    if (search) queryParams.q = search
    if (limit) queryParams.limit = limit
    if (offset) queryParams.offset = offset

    try {
      const { data } = await request.get<AuthorizationRelationPaginatedResponse>(
        `${this.getCompletePath()}/station_exceptionalities_company/${resourceId}`,
        queryParams
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as permissões de excepcionalidades.')
    }
  }

  async addStationExceptionalityToHealthUnit(
    resourceId: number,
    companyId: number,
    payload: UpsertPermissionsRequest
  ): Promise<AuthorizationRelationResponse[]> {
    try {
      const { data } = await request.post<AuthorizationRelationResponse[]>(
        `${this.getCompletePath()}/shared/station_exceptionalities_health_unit/${resourceId}/company/${companyId}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível adicionar as permissões de excepcionalidades.')
    }
  }

  async upsertStationExceptionalityToHealthUnit(
    resourceId: number,
    companyId: number,
    payload: UpsertPermissionsRequest
  ): Promise<AuthorizationRelationResponse[]> {
    try {
      const { data } = await request.put<AuthorizationRelationResponse[]>(
        `${this.getCompletePath()}/shared/station_exceptionalities_health_unit/${resourceId}/company/${companyId}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível atualizar as permissões de excepcionalidades.')
    }
  }

  async listStationExceptionalitiesByHealthUnit(
    resourceId: number,
    companyId: number,
    search?: string,
    limit?: number,
    offset?: number
  ): Promise<AuthorizationRelationPaginatedResponse> {
    const queryParams: PermissionListQuery = {}
    if (search) queryParams.q = search
    if (limit) queryParams.limit = limit
    if (offset) queryParams.offset = offset

    try {
      const { data } = await request.get<AuthorizationRelationPaginatedResponse>(
        `${this.getCompletePath()}/station_exceptionalities_health_unit/${resourceId}/company/${companyId}`,
        queryParams
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as permissões de excepcionalidades.')
    }
  }

  // ===========================================================================
  // STATION EXCEPTIONALITIES SCHEDULE (nível escala)
  // ===========================================================================

  async addStationExceptionalityToSchedule(
    resourceId: number,
    companyId: number,
    payload: UpsertPermissionsRequest
  ): Promise<AuthorizationRelationResponse[]> {
    try {
      const { data } = await request.post<AuthorizationRelationResponse[]>(
        `${this.getCompletePath()}/shared/station_exceptionalities_schedule/${resourceId}/company/${companyId}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível adicionar as permissões de excepcionalidades.')
    }
  }

  async upsertStationExceptionalityToSchedule(
    resourceId: number,
    companyId: number,
    payload: UpsertPermissionsRequest
  ): Promise<AuthorizationRelationResponse[]> {
    try {
      const { data } = await request.put<AuthorizationRelationResponse[]>(
        `${this.getCompletePath()}/shared/station_exceptionalities_schedule/${resourceId}/company/${companyId}`,
        payload
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível atualizar as permissões de excepcionalidades.')
    }
  }

  async listStationExceptionalitiesBySchedule(
    resourceId: number,
    companyId: number,
    search?: string,
    limit?: number,
    offset?: number
  ): Promise<AuthorizationRelationPaginatedResponse> {
    const queryParams: PermissionListQuery = {}
    if (search) queryParams.q = search
    if (limit) queryParams.limit = limit
    if (offset) queryParams.offset = offset

    try {
      const { data } = await request.get<AuthorizationRelationPaginatedResponse>(
        `${this.getCompletePath()}/station_exceptionalities_schedule/${resourceId}/company/${companyId}`,
        queryParams
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar as permissões de excepcionalidades.')
    }
  }
}
